import { ChevronRight, Check, Circle, FileText, Flag, Globe, Pencil, Search, Sparkles } from 'lucide-react';
import type { ActivityIcon, ActivityPhase, ActivityStatus, ActivityStep } from './types';
import { APP } from '../tokens';

const F = APP.fontFamily;
const THINKING_SERIF = '"Iowan Old Style", "Palatino Linotype", Palatino, Georgia, "Times New Roman", serif';

interface CoTActivityCardProps {
  phases: ActivityPhase[];
  onTogglePhase?: (phaseId: string) => void;
  onToggleStep?: (phaseId: string, stepId: string) => void;
}

function iconFor(step: ActivityStep, color: string) {
  if (step.kind === 'thinking') return <Sparkles size={11} color={color} />;
  if (step.kind === 'clarification') return <Circle size={9} color={color} />;
  const icon = step.icon as ActivityIcon | undefined;
  switch (icon) {
    case 'search':
    case 'found':
      return <Search size={11} color={color} />;
    case 'edit':
      return <Pencil size={11} color={color} />;
    case 'web':
    case 'mls':
      return <Globe size={11} color={color} />;
    case 'check':
      return <Check size={11} color={color} />;
    case 'flag':
      return <Flag size={11} color={color} />;
    case 'draft':
    case 'file':
    default:
      return <FileText size={11} color={color} />;
  }
}

function statusColor(status: ActivityStatus): string {
  switch (status) {
    case 'active': return '#D97645';
    case 'success': return '#2D6E5A';
    case 'error': return '#D94545';
    case 'pending': return '#C5BFB8';
  }
}

function Node({ step }: { step: ActivityStep }) {
  const color = statusColor(step.status);
  if (step.status === 'active') {
    return (
      <div
        style={{
          width: 17,
          height: 17,
          borderRadius: '50%',
          backgroundColor: APP.bgCardBody,
          border: `1.5px solid ${color}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 0 0 3px ${color}1A`,
        }}
      >
        {iconFor(step, color)}
      </div>
    );
  }
  return (
    <div
      style={{
        width: step.status === 'pending' ? 9 : 15,
        height: step.status === 'pending' ? 9 : 15,
        borderRadius: '50%',
        backgroundColor: step.status === 'pending' ? APP.bgCardBody : color,
        border: step.status === 'pending' ? `1.2px solid ${color}` : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {step.status !== 'pending' && iconFor(step, '#FFFFFF')}
    </div>
  );
}

function stepLabel(step: ActivityStep): string {
  if (step.kind === 'clarification') return `Awaiting clarification: ${step.question}`;
  return step.label;
}

function ActivityRow({
  phaseId,
  step,
  onToggleStep,
}: {
  phaseId: string;
  step: ActivityStep;
  onToggleStep?: (phaseId: string, stepId: string) => void;
}) {
  const isThinking = step.kind === 'thinking';
  const expanded = isThinking && step.expanded;
  const body = isThinking ? step.body : step.kind === 'tool_result' ? step.body : undefined;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '22px minmax(0, 1fr)', columnGap: 8, position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'center', paddingTop: 1, zIndex: 1 }}>
        <Node step={step} />
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, paddingBottom: 10 }}>
        <button
          type="button"
          onClick={isThinking ? () => onToggleStep?.(phaseId, step.id) : undefined}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            width: '100%',
            padding: 0,
            background: 'transparent',
            border: 'none',
            cursor: isThinking ? 'pointer' : 'default',
            textAlign: 'left',
            fontFamily: F,
          }}
        >
          {isThinking && (
            <ChevronRight
              size={12}
              color={APP.textMuted}
              style={{ transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 150ms ease', flexShrink: 0 }}
            />
          )}
          <span
            style={{
              color: step.status === 'active' ? APP.textDark : APP.textCardBody,
              fontSize: 12,
              fontWeight: step.status === 'active' ? 600 : 500,
              lineHeight: '16px',
              overflowWrap: 'anywhere',
            }}
          >
            {stepLabel(step)}
          </span>
        </button>
        {body && (!isThinking || expanded) && (
          <span
            style={{
              color: isThinking ? '#8B8178' : APP.textMuted,
              fontSize: isThinking ? 13 : 11,
              lineHeight: isThinking ? '20px' : '16px',
              fontFamily: isThinking ? THINKING_SERIF : F,
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              paddingLeft: isThinking ? 2 : 0,
            }}
          >
            {body}
          </span>
        )}
      </div>
    </div>
  );
}

function PhaseCard({
  phase,
  onTogglePhase,
  onToggleStep,
}: {
  phase: ActivityPhase;
  onTogglePhase?: (phaseId: string) => void;
  onToggleStep?: (phaseId: string, stepId: string) => void;
}) {
  if (phase.steps.length === 0) return null;

  if (phase.collapsed) {
    const hasError = phase.steps.some((step) => step.status === 'error');
    return (
      <button
        type="button"
        onClick={() => onTogglePhase?.(phase.id)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 7,
          width: '100%',
          borderRadius: 9,
          paddingBlock: 10,
          paddingInline: 12,
          border: `1px solid ${APP.borderCard}`,
          backgroundColor: APP.bgContent,
          color: hasError ? '#D94545' : APP.textCardBody,
          cursor: 'pointer',
          fontFamily: F,
          fontSize: 12,
          fontWeight: 500,
          lineHeight: '16px',
          boxShadow: '0 1px 2px rgba(26, 23, 20, 0.04)',
        }}
      >
        <Check size={13} />
        Completed in {phase.steps.length} step{phase.steps.length === 1 ? '' : 's'}
        <ChevronRight size={13} style={{ marginLeft: 'auto' }} />
      </button>
    );
  }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 9,
        paddingBlock: 12,
        paddingInline: 12,
        border: `1px solid ${APP.borderCard}`,
        backgroundColor: APP.bgContent,
        boxShadow: '0 1px 2px rgba(26, 23, 20, 0.04)',
      }}
    >
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: 21,
          top: 18,
          bottom: 18,
          width: 1,
          background: `linear-gradient(180deg, ${APP.border} 0%, ${APP.border} 100%)`,
          opacity: 0.85,
        }}
      />
      {phase.steps.map((step) => (
        <ActivityRow
          key={step.id}
          phaseId={phase.id}
          step={step}
          onToggleStep={onToggleStep}
        />
      ))}
    </div>
  );
}

export default function CoTActivityCard({ phases, onTogglePhase, onToggleStep }: CoTActivityCardProps) {
  if (phases.length === 0) return null;
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        overflowY: 'visible',
        overflowX: 'hidden',
      }}
    >
      {phases.map((phase) => (
        <PhaseCard
          key={phase.id}
          phase={phase}
          onTogglePhase={onTogglePhase}
          onToggleStep={onToggleStep}
        />
      ))}
    </div>
  );
}
